import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Home, Mail, Phone, Clock, Send } from "lucide-react";

export default function Contact() {
  const blank = { name: "", email: "", subject: "", message: "" };
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields"); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/support", form);
      toast.success(data.message || "Message sent to our support desk");
      setForm(blank);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <BrandLogo />
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2 font-semibold" data-testid="home-button"><Home className="h-4 w-4" /> Home</Button>
          </Link>
        </div>
      </header>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:px-8">
        {/* Left — info */}
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600">Support</span>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-5xl">Talk to our team.</h1>
          <p className="mt-4 max-w-md text-lg leading-relaxed text-slate-600">
            Experiencing an issue, or have a question about registration, document verification, or how the
            Wonderstone SMME Portal works? Drop us a line — we usually respond within one business day.
          </p>
          <div className="mt-10 space-y-6">
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><Mail className="h-4 w-4" /> Email</div>
              <a href="mailto:support@wonderstonesmmeportal.com" className="mt-1 block text-slate-800 hover:text-amber-600" data-testid="contact-email">support@wonderstonesmmeportal.com</a>
            </div>
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><Phone className="h-4 w-4" /> Phone</div>
              <a href="tel:+27738377509" className="mt-1 block text-slate-800 hover:text-amber-600" data-testid="contact-phone">073 8377 509</a>
            </div>
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><Clock className="h-4 w-4" /> Hours</div>
              <p className="mt-1 text-slate-800">Mon – Fri · 08h00 – 17h00 SAST</p>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
          <form onSubmit={submit} className="space-y-5" data-testid="support-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Name <span className="text-red-500">*</span></Label>
                <Input required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="support-name-input" />
              </div>
              <div>
                <Label>Email <span className="text-red-500">*</span></Label>
                <Input required type="email" value={form.email} onChange={set("email")} className="mt-1.5" data-testid="support-email-input" />
              </div>
            </div>
            <div>
              <Label>Subject <span className="text-red-500">*</span></Label>
              <Input required value={form.subject} onChange={set("subject")} className="mt-1.5" placeholder="e.g. Cannot upload my BBBEE certificate" data-testid="support-subject-input" />
            </div>
            <div>
              <Label>Message <span className="text-red-500">*</span></Label>
              <Textarea required rows={7} value={form.message} onChange={set("message")} className="mt-1.5" placeholder="Describe the issue you are experiencing…" data-testid="support-message-input" />
            </div>
            <Button type="submit" disabled={loading} className="gap-2 bg-slate-900 px-6 font-semibold hover:bg-slate-800" data-testid="support-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send message
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
